/**
 * Full-pipeline wizard (§5.2): a stepper that grows with the declared DAG.
 *
 * Nothing pipeline-specific is hardwired: the DAG comes from ?dag= (with a
 * picker when several pipelines are declared, auto-select when one), the
 * identity/params step is sourced from the tool whose NAME matches the
 * DAG's declared `identity_tool` (and skipped entirely when the DAG
 * declares none), and the upload zone is labeled from that tool's first
 * declared input slot.
 *
 * After upload the entry file goes through the same preflight as the
 * single-tool page (against the identity tool), so "wrong file for this
 * pipeline" is a message here instead of a dead job. Launch stays gated
 * when the identity-param source failed to load: an empty list because
 * /api/tools errored is NOT "no fields required".
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Stack,
  Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { ApiError, api, type PendingApproval } from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { useEditMode } from "../hooks/useCustomer";
import { useJobPolling } from "../hooks/useJobPolling";
import { usePageTitle } from "../hooks/usePageTitle";
import { categoryOf, pollIntervalMs } from "../lib/states";
import { ID_SLICE_LEN } from "../lib/config";
import {
  dagDescription,
  dagLabel,
  pickDag,
  resolveEntryTool,
  resolveIdentityTool,
} from "../lib/wizard";
import { resolveText } from "../lib/tooltext";
import { UploadZone } from "../components/UploadZone";
import { PreflightPanel, type PreflightState } from "../components/PreflightPanel";
import { SourceTabs } from "../components/SourceTabs";
import { ParamsForm } from "../components/ParamsForm";
import { inputRequired, missingRequired, saveStickyParams, tabsParam } from "../lib/params";
import { ApprovalDialog } from "../components/ApprovalDialog";
import { ArtifactFiles } from "../components/ArtifactFiles";
import { ValidationReportView } from "../components/ValidationReportView";
import { PageHeader } from "../components/ui/PageHeader";
import { SectionLabel } from "../components/ui/SectionLabel";
import { StateChip } from "../components/ui/StateChip";
import { ErrorAlert } from "../components/ui/ErrorAlert";
import { EmptyState } from "../components/ui/EmptyState";

export function WizardPage() {
  const { t, i18n } = useTranslation();
  const edit = useEditMode();
  usePageTitle("wizard.title");
  const [searchParams, setSearchParams] = useSearchParams();
  const [wizardStep, setWizardStep] = useState(0);
  const [artifactId, setArtifactId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [jobId, setJobId] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<PendingApproval | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [preflight, setPreflight] = useState<PreflightState>(null);

  const dagsQuery = useApiQuery(["dags"], api.dags);
  const dags = useMemo(() => dagsQuery.data ?? [], [dagsQuery.data]);
  const dagParam = searchParams.get("dag");
  const dag = useMemo(() => pickDag(dags, dagParam), [dags, dagParam]);

  function selectDag(name: string | null) {
    const next = new URLSearchParams(searchParams);
    if (name) next.set("dag", name);
    else next.delete("dag");
    setSearchParams(next, { replace: true });
  }

  // A different pipeline is a different run context: reset the wizard when
  // the selected DAG changes (URL navigation included).
  const dagName = dag?.name ?? null;
  useEffect(() => {
    setWizardStep(0);
    setArtifactId(null);
    setJobId(null);
    setFailure(null);
    setPreflight(null);
    setValues({});
  }, [dagName]);

  // Identity fields come from the tool whose NAME matches the DAG's
  // declared identity_tool (matched by name, never by key).
  const toolsQuery = useApiQuery(["tools"], api.tools);
  const hasIdentityStep = Boolean(dag?.identity_tool);
  const identityTool = useMemo(
    () =>
      toolsQuery.data && dag?.identity_tool
        ? resolveIdentityTool(dag.identity_tool, toolsQuery.data)
        : null,
    [toolsQuery.data, dag],
  );
  const toolParams = useMemo(() => identityTool?.params ?? [], [identityTool]);
  // The gate must stay shut when the param source failed or the declared
  // tool is missing from the catalogue — an empty list because /api/tools
  // errored is NOT "no fields required".
  const identityUnavailable =
    hasIdentityStep &&
    (toolsQuery.isError || (toolsQuery.isSuccess && identityTool === null));

  // The upload goes to the pipeline's FIRST step. When that is not the
  // identity tool (a pipeline starting from a raw file), its own operator
  // parameters — the data source — are asked with the upload, and the file
  // may not be needed at all (a direct source pull).
  const entryTool = useMemo(
    () => (toolsQuery.data ? resolveEntryTool(dag, toolsQuery.data) : null),
    [toolsQuery.data, dag],
  );
  const entryParams = useMemo(() => {
    if (!entryTool || entryTool.name === identityTool?.name) return [];
    const identityNames = new Set(toolParams.map((p) => p.name));
    return entryTool.params.filter((p) => !identityNames.has(p.name));
  }, [entryTool, identityTool, toolParams]);
  const needsFile = useMemo(
    () => (entryTool ? inputRequired(entryTool, values) : true),
    [entryTool, values],
  );
  const sourceTabs = useMemo(() => tabsParam(entryParams), [entryParams]);

  // Preflight parity with the single-tool page: check the entry file
  // against the tool that will read it the moment both are known.
  const entryKey = entryTool?.key ?? null;
  useEffect(() => {
    if (!entryKey || !artifactId) {
      setPreflight(null);
      return;
    }
    let cancelled = false;
    setPreflight("checking");
    api
      .preflightTool({ tool: entryKey, artifact_id: artifactId })
      .then((r) => {
        if (!cancelled) setPreflight({ ok: r.ok, errors: r.errors });
      })
      .catch(() => {
        if (!cancelled) setPreflight(null);
      });
    return () => {
      cancelled = true;
    };
  }, [entryKey, artifactId]);

  const mismatch =
    needsFile && preflight !== null && preflight !== "checking" && !preflight.ok;
  const fileMissing = needsFile && !artifactId;

  // Shared job-polling hook: terminal stop, backoff, visibility pause, abort.
  const { job, terminal } = useJobPolling(jobId);

  // Poll pending approvals while this run is live and not terminal.
  const pendingQuery = useApiQuery(["approvals", "pending"], api.pending, {
    pollMs: pollIntervalMs(),
    enabled: Boolean(jobId) && !terminal,
  });
  const pending = useMemo(
    () => (pendingQuery.data ?? []).filter((p) => p.job_id === jobId),
    [pendingQuery.data, jobId],
  );

  const identityMissing = useMemo(
    () => missingRequired(toolParams, values, true),
    [toolParams, values],
  );
  // the entry tool's own required parameters (a source pull's company code…)
  const entryMissing = useMemo(
    () => missingRequired(entryParams, values, false),
    [entryParams, values],
  );

  // Step indices grow with the declared shape: the identity step only
  // exists when the DAG declares an identity tool.
  const runStepIndex = hasIdentityStep ? 2 : 1;

  async function launch() {
    if (!dag || fileMissing) return;
    setBusy(true);
    setFailure(null);
    try {
      saveStickyParams([...toolParams, ...entryParams], values);
      const supplied = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v.trim() !== ""),
      );
      const started = await api.startPipeline(
        dag.name,
        needsFile ? (artifactId ?? undefined) : undefined,
        supplied,
      );
      setJobId(started.job_id);
      setWizardStep(runStepIndex);
    } catch (err) {
      setFailure(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setWizardStep(0);
    setArtifactId(null);
    setJobId(null);
    setFailure(null);
    setPreflight(null);
  }

  if (dagsQuery.isPending) {
    return (
      <Stack spacing={2}>
        <PageHeader title={t("wizard.title")} />
        <LinearProgress />
      </Stack>
    );
  }

  // A failed DAG fetch is an error with retry — not "no pipeline declared".
  if (dagsQuery.isError) {
    return (
      <Stack spacing={2}>
        <PageHeader title={t("wizard.title")} />
        <ErrorAlert error={dagsQuery.error} onRetry={() => void dagsQuery.refetch()} />
      </Stack>
    );
  }

  if (dags.length === 0) {
    return (
      <Stack spacing={2}>
        <PageHeader title={t("wizard.title")} />
        <EmptyState
          icon={<AccountTreeIcon fontSize="large" />}
          message={t("wizard.noDag")}
        />
      </Stack>
    );
  }

  // Several pipelines and none selected → the picker.
  if (!dag) {
    return (
      <Stack spacing={3}>
        <PageHeader title={t("wizard.title")} subtitle={t("wizard.subtitle")} />
        <Box>
          <SectionLabel>{t("wizard.pickDag")}</SectionLabel>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {dags.map((entry) => (
              <Grid item xs={12} sm={6} md={4} key={entry.name}>
                <Card sx={{ height: "100%" }}>
                  <CardActionArea
                    sx={{ height: "100%" }}
                    onClick={() => selectDag(entry.name)}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {dagLabel(entry, i18n.language)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {t("wizard.stepCount", { count: entry.steps.length })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {dagDescription(entry, i18n.language) ||
                          entry.steps
                            .map((s) => resolveText(s.description, i18n.language))
                            .filter(Boolean)
                            .join(" ")}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    );
  }

  const jobSteps = job?.steps ?? [];
  const stepStateByKey = new Map(jobSteps.map((s) => [s.step_key, s]));
  const slotPrompt =
    resolveText(entryTool?.input_slots?.[0]?.label, i18n.language) || undefined;

  const launchButton = (
    <Button
      variant="contained"
      disabled={
        !edit.canEdit ||
        fileMissing ||
        mismatch ||
        entryMissing.length > 0 ||
        identityUnavailable ||
        (hasIdentityStep && (toolsQuery.isPending || identityMissing.length > 0)) ||
        busy
      }
      onClick={() => void launch()}
    >
      {busy ? t("wizard.launching") : t("wizard.launch")}
    </Button>
  );

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t("wizard.title")}
        subtitle={t("wizard.subtitle")}
        extra={
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {dagLabel(dag, i18n.language)} ·{" "}
              {t("wizard.stepCount", { count: dag.steps.length })}
            </Typography>
            {dags.length > 1 && (
              <Button size="small" onClick={() => selectDag(null)}>
                {t("wizard.changeDag")}
              </Button>
            )}
          </Stack>
        }
      />

      <Card>
        <CardContent>
          <Stepper activeStep={wizardStep} orientation="vertical">
            <Step>
              <StepLabel>{t("wizard.stepUpload")}</StepLabel>
              <StepContent>
                {sourceTabs && entryTool && (
                  <SourceTabs
                    param={sourceTabs}
                    params={entryTool.params}
                    values={values}
                    onChange={setValues}
                  />
                )}
                {entryParams.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t("wizard.entryParams")}
                    </Typography>
                    <ParamsForm params={entryParams} values={values} onChange={setValues} />
                  </Box>
                )}
                {needsFile ? (
                  <>
                    <UploadZone
                      prompt={slotPrompt}
                      onUploaded={(result) => {
                        setArtifactId(result.artifact_id);
                        setFailure(null);
                      }}
                    />
                    <PreflightPanel preflight={preflight} />
                  </>
                ) : (
                  <Alert severity="info">{t("wizard.noFileNeeded")}</Alert>
                )}
                <Box sx={{ mt: 2 }}>
                  {hasIdentityStep ? (
                    <Button
                      variant="contained"
                      disabled={fileMissing || mismatch || entryMissing.length > 0}
                      onClick={() => setWizardStep(1)}
                    >
                      {t("wizard.next")}
                    </Button>
                  ) : (
                    launchButton
                  )}
                  {fileMissing && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      {t("wizard.uploadFirst")}
                    </Typography>
                  )}
                </Box>
              </StepContent>
            </Step>

            {hasIdentityStep && (
              <Step>
                <StepLabel>{t("wizard.stepIdentity")}</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("wizard.identityHelp")}
                  </Typography>
                  {toolsQuery.isPending && <LinearProgress sx={{ mb: 2 }} />}
                  {identityUnavailable && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {t("wizard.identityUnavailable")}
                      <Box sx={{ mt: 1 }}>
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => void toolsQuery.refetch()}
                        >
                          {t("common.retry")}
                        </Button>
                      </Box>
                    </Alert>
                  )}
                  {!identityUnavailable && !toolsQuery.isPending && (
                    <ParamsForm
                      params={toolParams}
                      values={values}
                      onChange={setValues}
                      requireAll
                    />
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button onClick={() => setWizardStep(0)}>{t("wizard.back")}</Button>
                    {launchButton}
                  </Stack>
                  {identityMissing.length > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("wizard.fillIdentity")}
                    </Typography>
                  )}
                </StepContent>
              </Step>
            )}

            <Step>
              <StepLabel>{t("wizard.stepRun")}</StepLabel>
              <StepContent>
                {!job && <LinearProgress />}
                {job && (
                  <>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <StateChip state={job.state} fallbackLabel={job.dashboard_status} />
                      <Typography variant="caption" className="num" color="text.secondary">
                        {job.job_id.slice(0, ID_SLICE_LEN)}
                      </Typography>
                    </Stack>

                    <Stepper orientation="vertical" nonLinear>
                      {dag.steps.map((dagStep) => {
                        const state = stepStateByKey.get(dagStep.key);
                        const awaiting = pending.find((p) => p.step_key === dagStep.key);
                        return (
                          <Step
                            key={dagStep.key}
                            active
                            completed={
                              state ? categoryOf(state.state) === "success" : false
                            }
                          >
                            <StepLabel
                              optional={
                                <Typography variant="caption" color="text.secondary">
                                  {resolveText(dagStep.description, i18n.language)}
                                </Typography>
                              }
                            >
                              {resolveText(
                                resolveIdentityTool(dagStep.tool, toolsQuery.data ?? [])
                                  ?.display_name,
                                i18n.language,
                              ) || dagStep.key}
                              {state && (
                                <StateChip state={state.state} sx={{ ml: 1 }} />
                              )}
                              {dagStep.hitl !== "NONE" && (
                                <Chip
                                  size="small"
                                  sx={{ ml: 0.5 }}
                                  variant="outlined"
                                  label={t("tools.hitl")}
                                />
                              )}
                            </StepLabel>
                            {awaiting && (
                              <StepContent>
                                <Alert
                                  severity="warning"
                                  action={
                                    <Button
                                      color="inherit"
                                      size="small"
                                      onClick={() => setReviewing(awaiting)}
                                    >
                                      {t("wizard.review")}
                                    </Button>
                                  }
                                >
                                  {t("wizard.awaiting")}
                                </Alert>
                              </StepContent>
                            )}
                          </Step>
                        );
                      })}
                    </Stepper>

                    {job.artifacts.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <SectionLabel>{t("wizard.files")}</SectionLabel>
                        <ArtifactFiles artifacts={job.artifacts} />
                      </Box>
                    )}
                    <Button sx={{ mt: 2 }} onClick={reset}>
                      {t("wizard.newRun")}
                    </Button>
                  </>
                )}
              </StepContent>
            </Step>
          </Stepper>
        </CardContent>
      </Card>

      {failure && (
        <ValidationReportView
          report={failure.body?.report}
          errors={failure.body?.errors ?? [failure.message]}
          diff={failure.body?.diff}
        />
      )}

      {reviewing && (
        <ApprovalDialog
          item={reviewing}
          onClose={() => setReviewing(null)}
          onDecided={() => void pendingQuery.refetch()}
        />
      )}
    </Stack>
  );
}
